import type { FacetDef, FilterDict } from './types';
import { nonEmptyFacetKeys } from './types';

export interface ActiveFilterChipsProps {
  filters: FilterDict;
  onFiltersChange: (next: FilterDict) => void;
  facets: ReadonlyArray<FacetDef>;
  /** Test-id prefix so each host page gets stable selectors. */
  testIdPrefix: string;
}

/**
 * One dismissible pill per non-empty facet — `Type: Bug, Story ×`. The ×
 * clears that facet from the URL. When no facet is set, renders nothing.
 */
export function ActiveFilterChips({
  filters,
  onFiltersChange,
  facets,
  testIdPrefix
}: ActiveFilterChipsProps) {
  const nonEmpty = nonEmptyFacetKeys(filters);
  if (nonEmpty.length === 0) return null;

  function clear(key: string) {
    onFiltersChange({ ...filters, [key]: [] });
  }

  function labelFor(key: string, valueId: string): string {
    const facet = facets.find((f) => f.key === key);
    const opt = facet?.options.find((o) => o.id === valueId);
    return opt?.label ?? valueId;
  }

  function facetLabel(key: string): string {
    return facets.find((f) => f.key === key)?.label ?? key;
  }

  return (
    <>
      {nonEmpty.map((key) => {
        const values = filters[key];
        const text = values.map((v) => labelFor(key, v)).join(', ');
        return (
          <span
            key={key}
            className="pn-listctl-active-chip"
            data-testid={`${testIdPrefix}-active-chip-${key}`}
          >
            {facetLabel(key)}: {text}
            <button
              type="button"
              className="pn-listctl-active-chip-x"
              aria-label={`Remove ${facetLabel(key)} filter`}
              data-testid={`${testIdPrefix}-active-chip-clear-${key}`}
              onClick={() => clear(key)}
            >
              ×
            </button>
          </span>
        );
      })}
    </>
  );
}
